/**
 * useAgentSkills — real capability data for the home-page layout sketches.
 *
 * The home carousel endpoint (`/chatbots/carousel`) is deliberately tiny: id,
 * name, description, avatar color/variant, allowed_models. It carries NO
 * skills. The sketches need capability tags and quick-start chips, so this
 * hook pulls the real thing from `/skills/chatbot/<id>` and maps it to a
 * small view model.
 *
 * Where the copy comes from — all REAL data, nothing invented:
 *   • tag / capability name → `skill.skill_name`
 *   • capability blurb      → `skill.skill_description`
 *   • quick-start chips     → `skill.skill_triggers` (the phrases that
 *                             actually activate the skill, so they double
 *                             as honest "try asking…" examples)
 *
 * An agent with no skills configured simply renders no tags — the variants
 * all degrade to name + description rather than showing placeholder text.
 *
 * Caching is module-level on purpose: flipping between variants A/B/C during
 * a stakeholder review must not refire the same requests.
 */

import { useEffect, useRef, useState } from 'react';
import { SkillsApi } from '@modules/skills/services/skillsApi';
import type { ChatbotSkill } from '@modules/skills/types';
import { CONCIERGE_CAROUSEL_ID } from '@modules/concierge/components/conciergeCarouselItem';

export interface AgentCapability {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  /** Phrases that trigger the skill — used as quick-start prompt chips. */
  triggers: string[];
}

/** Resolved capabilities per chatbot id. Survives variant switches. */
const capabilityCache = new Map<string, AgentCapability[]>();
/** De-dupes concurrent requests for the same bot (cards + drawer race). */
const inflight = new Map<string, Promise<AgentCapability[]>>();

/** How many skill lookups may be in flight at once during a bulk prefetch. */
const PREFETCH_CONCURRENCY = 4;

function toCapabilities(rows: ChatbotSkill[]): AgentCapability[] {
  return rows
    .filter(row => row.is_enabled !== false && !!row.skill)
    .slice()
    .sort((a, b) => (a.skill_priority ?? 0) - (b.skill_priority ?? 0))
    .map(row => ({
      id: row.skill_id,
      name: row.skill!.skill_name,
      description: row.skill!.skill_description,
      category: row.skill!.skill_category,
      triggers: Array.isArray(row.skill!.skill_triggers) ? row.skill!.skill_triggers : [],
    }));
}

/**
 * Fetch (or reuse) one agent's capabilities.
 *
 * A failed request is NOT cached — a transient 500 during a review would
 * otherwise permanently blank that agent's tags for the rest of the session.
 */
function loadCapabilities(botId: string): Promise<AgentCapability[]> {
  const cached = capabilityCache.get(botId);
  if (cached) return Promise.resolve(cached);

  const existing = inflight.get(botId);
  if (existing) return existing;

  const request = SkillsApi.getChatbotSkills(botId)
    .then(rows => {
      const caps = toCapabilities(rows || []);
      capabilityCache.set(botId, caps);
      return caps;
    })
    .catch(() => [] as AgentCapability[])
    .finally(() => { inflight.delete(botId); });

  inflight.set(botId, request);
  return request;
}

/** True for ids we must never send to the skills endpoint. */
function isFetchable(botId: string | null | undefined): botId is string {
  return !!botId && botId !== CONCIERGE_CAROUSEL_ID;
}

/**
 * Capabilities for a SINGLE agent — used by the Variant A drawer and the
 * Variant B hover popover, where only the focused agent matters.
 */
export function useAgentSkills(botId: string | null) {
  const [capabilities, setCapabilities] = useState<AgentCapability[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isFetchable(botId)) {
      setCapabilities([]);
      setLoading(false);
      return;
    }
    const cached = capabilityCache.get(botId);
    if (cached) {
      setCapabilities(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    loadCapabilities(botId).then(caps => {
      if (cancelled) return;
      setCapabilities(caps);
      setLoading(false);
    });
    return () => { cancelled = true; };
  }, [botId]);

  return { capabilities, loading };
}

/**
 * Capabilities for EVERY agent on screen — used by the grid variants, which
 * show tags on each card rather than only on the focused one.
 *
 * Runs a small worker pool (PREFETCH_CONCURRENCY) instead of firing one
 * request per agent simultaneously: a tenant with 40 agents would otherwise
 * open 40 parallel connections the moment the home page paints.
 *
 * `enabled` lets a variant opt out entirely — Variant C shows no skill tags,
 * so mounting it must not trigger any of this traffic.
 */
export function useAllAgentSkills(botIds: string[], enabled: boolean) {
  const [byBotId, setByBotId] = useState<Record<string, AgentCapability[]>>({});
  // Key the effect on the id list's identity, not the array reference — the
  // parent rebuilds `carouselBots` on every render.
  const idsKey = botIds.join(',');
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const targets = botIds.filter(isFetchable);
    if (targets.length === 0) return;

    // Seed synchronously from cache so a variant switch paints tags on the
    // first frame instead of flashing empty cards.
    const seeded: Record<string, AgentCapability[]> = {};
    for (const id of targets) {
      const cached = capabilityCache.get(id);
      if (cached) seeded[id] = cached;
    }
    if (Object.keys(seeded).length > 0) {
      setByBotId(prev => ({ ...prev, ...seeded }));
    }

    const pending = targets.filter(id => !capabilityCache.has(id));
    if (pending.length === 0) return;

    let cancelled = false;
    const queue = [...pending];

    const worker = async (): Promise<void> => {
      while (!cancelled) {
        const next = queue.shift();
        if (!next) return;
        const caps = await loadCapabilities(next);
        if (cancelled || !mountedRef.current) return;
        setByBotId(prev => ({ ...prev, [next]: caps }));
      }
    };

    const pool = Array.from(
      { length: Math.min(PREFETCH_CONCURRENCY, queue.length) },
      () => worker(),
    );
    // Errors are already swallowed per-request inside loadCapabilities; this
    // catch only guards against an unexpected throw in the pool itself.
    Promise.all(pool).catch(() => { /* non-fatal — cards just render untagged */ });

    return () => { cancelled = true; };
  }, [idsKey, enabled]);

  return byBotId;
}
